import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { FC, useContext, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { Col, Row, Divider } from "antd";
import AppContext from "../../../context/app/context";
import { IStoreProductStat } from "../../../models/statistcs/respons";
import i18n from "../../../utils/localization/i18n";
import styles from "./style.module.scss";

ChartJS.register(ArcElement, Tooltip, Legend);

interface IProps {
  stats?: IStoreProductStat[];
}

const StoreProductStats: FC<IProps> = ({ stats }) => {
  const { t } = useTranslation();
  const { theme } = useContext(AppContext);

  if (!stats || stats.length === 0) return null;

  return (
    <div className={styles.container}>
      <Divider orientation="center">{t("store_product_stats")}</Divider>
      <Row gutter={[16, 16]}>
        {stats.map((store) => (
          <Col key={store.id} xs={24} sm={12} md={8} lg={6}>
            <StoreChart store={store} theme={theme} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

interface IStoreChartProps {
  store: IStoreProductStat;
  theme: string;
}

const StoreChart: FC<IStoreChartProps> = ({ store, theme }) => {
  const { t } = useTranslation();
  const lang = i18n.language as "ar" | "en";
  const storeName =
    typeof store.name === "string"
      ? store.name
      : store.name?.[lang] || store.name?.ar;
  const available = store.available_active_products_count;
  const total = store.active_products_count;
  const unavailable = total - available;

  const data = useMemo(
    () => ({
      labels: [t("unavailable_products"), t("available_products")],
      datasets: [
        {
          data: [unavailable, available],
          backgroundColor: [
            "rgba(245, 34, 45, 0.7)",
            "rgba(82, 196, 26, 0.25)",
          ],
          borderColor: ["rgba(245, 34, 45, 0.9)", "rgba(82, 196, 26, 0.4)"],
          borderWidth: 1.3,
          hoverOffset: 8,
          hoverBorderWidth: 4,
        },
      ],
    }),
    [available, unavailable, t]
  );

  const centerTextPlugin = useMemo(
    () => ({
      id: "centerText",
      beforeDraw(chart: any) {
        const { ctx, width, height } = chart;
        ctx.save();

        // Unavailable count - big red number
        const countText = `${unavailable}`;
        ctx.font = "bold 22px sans-serif";
        ctx.fillStyle = unavailable > 0 ? "#f5222d" : "#52c41a";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(countText, width / 2, height / 2 - 8);

        // "from total" label below
        const fromText = `/ ${total}`;
        ctx.font = "12px sans-serif";
        ctx.fillStyle = theme === "dark" ? "#aaa" : "#999";
        ctx.fillText(fromText, width / 2, height / 2 + 12);

        ctx.restore();
      },
    }),
    [unavailable, total, theme]
  );

  const options = useMemo(
    () => ({
      cutout: "65%",
      rotation: -90,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || "";
              const value = context.parsed || 0;
              const pct =
                total > 0 ? Math.round((value / total) * 100) : 0;
              return `${label}: ${value} (${pct}%)`;
            },
          },
        },
      },
    }),
    [total]
  );

  return (
    <div className={styles.chartCard}>
      <div className={styles.storeName} title={storeName}>
        {storeName}
      </div>
      <div className={styles.chartWrapper}>
        <Doughnut data={data} options={options} plugins={[centerTextPlugin]} />
      </div>
      <div className={styles.statsRow}>
        <span className={styles.unavailableCount}>{unavailable}</span>
        <span className={styles.unavailableLabel}>
          {t("unavailable_from_total", { total })}
        </span>
      </div>
    </div>
  );
};

export default StoreProductStats;
